import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useSearchParams } from 'react-router-dom';
import { db, COLLECTIONS } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  serverTimestamp,
  query,
  where,
  getDocs,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Clock, 
  Calendar,
  Users,
  CheckSquare,
  RotateCcw,
  AlertCircle,
  Building2,
  X,
  Save,
  Eye,
  TrendingUp,
  UserCheck,
  UserX,
  Filter,
  BarChart3,
  CalendarDays,
  CalendarRange
} from 'lucide-react';
import { cleanupOrphanedTasks } from '../utils/dailyRoutineScheduler';

const DailyRoutines = () => {
  const { currentUser } = useAuth();
  const { users, departments, teams, tasks, createTask } = useData();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // All users can manage routines
  const canManageRoutines = true;
  const canManagePersonalRoutines = true;
  
  const [routines, setRoutines] = useState([]);
  const [showNewRoutineModal, setShowNewRoutineModal] = useState(false);
  const [showEditRoutineModal, setShowEditRoutineModal] = useState(false);
  const [showExecutionStatusModal, setShowExecutionStatusModal] = useState(false);
  const [selectedRoutineForStatus, setSelectedRoutineForStatus] = useState(null);
  const [executionStatuses, setExecutionStatuses] = useState([]);
  const [editingRoutine, setEditingRoutine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completedToday, setCompletedToday] = useState(new Set());
  const [activeTab, setActiveTab] = useState('organizational');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [allExecutionStatuses, setAllExecutionStatuses] = useState([]);

  const [routineForm, setRoutineForm] = useState({
    name: '',
    description: '',
    assigneeId: '', // For single assignee (legacy support)
    assigneeIds: [], // For multiple assignees
    departmentId: '',
    teamId: '',
    priority: 'medium',
    estimatedTime: 30,
    dueTime: '09:00',
    scope: 'personal', // ברירת מחדל אישי לכולם
    isActive: true,
    frequency: 'daily', // daily, weekly, monthly
    weeklyDay: 1, // 0=Sunday, 1=Monday, etc.
    monthlyDate: 1, // 1-31
      quarterlyMonth: 1,    // הוסף את זה
  quarterlyDay: 1,      // הוסף את זה
  yearlyMonth: 1,       // הוסף את זה
  yearlyDay: 1,   
    enableNotifications: true,
    notificationTime: '08:30' // Time to send reminder
  });

  // Load routines from Firebase
  useEffect(() => {
    loadRoutines();
  }, []);

  // Check for action=create parameter
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'create') {
      setShowNewRoutineModal(true);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const loadRoutines = async () => {
    try {
      setLoading(true);
      const routinesRef = collection(db, COLLECTIONS.DAILY_ROUTINES || 'dailyRoutines');
      const snapshot = await getDocs(routinesRef);
      const allRoutines = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const filteredRoutines = allRoutines.filter(routine => {
        if (routine.scope === 'personal') {
          return routine.createdBy === currentUser.id;
        }
        
        if (routine.scope === 'managerial') {
          if (currentUser.role === 'ceo' || currentUser.role === 'deputy_ceo' || currentUser.role === 'org_admin') {
            return true;
          }
          
          // אם השגרה מיועדת לכל המחלקות, כל העובדים רואים אותה
          if (routine.departmentId === 'all') {
            return true;
          }
          
          return routine.departmentId === currentUser.departmentId;
        }
        
        return false;
      });
      
      setRoutines(filteredRoutines);
      
      // Initialize completed today state
      const today = new Date().toISOString().split('T')[0];
      const completedIds = new Set();
      
      for (const routine of filteredRoutines) {
        const executions = await getRoutineExecutions(routine.id, today);
        if (executions.length > 0) {
          completedIds.add(routine.id);
        }
      }
      
      setCompletedToday(completedIds);
      
    } catch (error) {
      console.error('Error loading routines:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get routine executions for a specific date
  const getRoutineExecutions = async (routineId, date) => {
    try {
      const executionsRef = collection(db, 'routineExecutions');
      const q = query(
        executionsRef, 
        where('routineId', '==', routineId),
        where('executionDate', '==', date)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting executions:', error);
      return [];
    }
  };

  // Helper function to check if user can view routine status
  const canViewRoutineStatus = (routine) => {
    // Personal routines - only creator and assignees can view
    if (routine.scope === 'personal') {
      const assigneeIds = routine.assigneeIds || [routine.assigneeId];
      if (assigneeIds.length > 0 && assigneeIds[0]) {
        return routine.createdBy === currentUser.id || assigneeIds.includes(currentUser.id);
      } else {
        // אם אין אחראים ספציפיים, רק היוצר יכול לראות
        return routine.createdBy === currentUser.id;
      }
    }
    
    // Organizational routines - based on role
    if (routine.scope === 'managerial') {
      // CEO and Deputy CEO can view all organizational routines
      if (currentUser.role === 'ceo' || currentUser.role === 'deputy_ceo') {
        return true;
      }
      
      // Department manager can view routines of their department
      if (currentUser.role === 'dept_manager') {
        return routine.departmentId === currentUser.departmentId || routine.departmentId === 'all';
      }
      
      // Team manager can view routines of their team
      if (currentUser.role === 'team_manager') {
        return routine.teamId === currentUser.teamId || 
               (routine.departmentId === currentUser.departmentId && !routine.teamId) ||
               routine.departmentId === 'all';
      }
      
      // Org admin can view all
      if (currentUser.role === 'org_admin') {
        return true;
      }
      
      // Employees can only view if they are assigned or if no specific assignees
      const assigneeIds = routine.assigneeIds || [routine.assigneeId];
      if (assigneeIds.length > 0 && assigneeIds[0]) {
        return assigneeIds.includes(currentUser.id);
      } else {
        // אם אין אחראים ספציפיים, כל העובדים יכולים לראות
        return true;
      }
    }
    
    return false;
  };

  // Check if routine should be active today based on frequency
const isRoutineScheduledForToday = (routine) => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const dayOfMonth = today.getDate();
  const month = today.getMonth() + 1; // 1-12
  
  switch (routine.frequency) {
    case 'daily':
      return true;
      
    case 'weekly':
      return dayOfWeek === (routine.weeklyDay || 1);
      
    case 'monthly':
      return dayOfMonth === (routine.monthlyDate || 1);
      
    case 'quarterly':
      // חישוב רבעון נוכחי
      const currentQuarter = Math.ceil(month / 3);
      const quarterStartMonth = (currentQuarter - 1) * 3 + 1;
      const targetMonth = quarterStartMonth + (routine.quarterlyMonth || 1) - 1;
      return month === targetMonth && dayOfMonth === (routine.quarterlyDay || 1);
      
    case 'yearly':
      return month === (routine.yearlyMonth || 1) && dayOfMonth === (routine.yearlyDay || 1);
      
    default:
      return true;
  }
};

  // Load execution status for a routine
  const loadExecutionStatus = async (routine) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const executions = await getRoutineExecutions(routine.id, today);
      
     // קבע אילו משתמשים רלוונטיים לשגרה
let relevantUsers = [];

if (routine.scope === 'managerial') {
  // שגרה ארגונית - כל העובדים הפעילים
  if (routine.departmentId && routine.departmentId !== 'all') {
    // אם יש מחלקה ספציפית - רק עובדי המחלקה
    relevantUsers = users.filter(u => 
      u.isActive && u.departmentId === routine.departmentId
    );
  } else {
    // אם אין מחלקה או נבחר "כל המחלקות" - כל העובדים הפעילים
    relevantUsers = users.filter(u => u.isActive);
  }
} else {
  // שגרה אישית - רק האחראים שנבחרו או היוצר אם לא נבחרו אחראים
  const assigneeIds = routine.assigneeIds || [routine.assigneeId];
  if (assigneeIds.length > 0 && assigneeIds[0]) {
    relevantUsers = assigneeIds.map(id => users.find(u => u.id === id)).filter(Boolean);
  } else {
    // אם לא נבחרו אחראים ספציפיים, השגרה חלה רק על היוצר
    relevantUsers = users.filter(u => u.id === routine.createdBy);
  }
}

// צור סטטוס לכל משתמש רלוונטי
const statuses = [];
for (const user of relevantUsers) {
  const execution = executions.find(e => e.userId === user.id);
  
  statuses.push({
    userId: user.id,
    userName: user.name,
    departmentId: user.departmentId,
    department: departments.find(d => d.id === user.departmentId)?.name,
    teamId: user.teamId,
    team: teams.find(t => t.id === user.teamId)?.name,
    status: execution ? 'completed' : 'pending',
    completedAt: execution?.completedAt?.toDate?.() || null,
    completedBy: execution?.completedBy
  });
}
      
      setExecutionStatuses(statuses);
      setAllExecutionStatuses(statuses);
    } catch (error) {
      console.error('Error loading execution status:', error);
    }
  };

  // Open execution status modal
  const openExecutionStatusModal = async (routine) => {
    // Check permissions before opening modal
    if (!canViewRoutineStatus(routine)) {
      alert('אין לך הרשאות לצפות בסטטוס של שגרה זו');
      return;
    }
    
    setSelectedRoutineForStatus(routine);
    await loadExecutionStatus(routine);
    setShowExecutionStatusModal(true);
  };

  // Helper function to check if user can undo routine completion for another user
  const canUndoRoutineCompletion = (routine, targetUserId) => {
    // User can always undo their own completion
    if (currentUser.id === targetUserId) {
      return true;
    }
    
    // Personal routines - only creator can undo for others
    if (routine.scope === 'personal') {
      return routine.createdBy === currentUser.id;
    }
    
    // Organizational routines - based on role hierarchy
    if (routine.scope === 'managerial') {
      // CEO and Deputy CEO can undo for anyone
      if (currentUser.role === 'ceo' || currentUser.role === 'deputy_ceo') {
        return true;
      }
      
      // Org admin can undo for anyone
      if (currentUser.role === 'org_admin') {
        return true;
      }
      
      const targetUser = users.find(u => u.id === targetUserId);
      if (!targetUser) return false;
      
      // Department manager can undo for their department members
      if (currentUser.role === 'dept_manager') {
        return targetUser.departmentId === currentUser.departmentId;
      }
      
      // Team manager can undo for their team members
      if (currentUser.role === 'team_manager') {
        return targetUser.teamId === currentUser.teamId;
      }
    }
    
    return false;
  };

  // Mark routine as completed by specific user
  const markRoutineAsCompleted = async (routine, userId) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const executionRef = doc(collection(db, 'routineExecutions'));
      
      await setDoc(executionRef, {
        routineId: routine.id,
        userId: userId,
        executionDate: today,
        completedAt: serverTimestamp(),
        completedBy: currentUser.id,
        routineName: routine.name
      });
      
      // Create task for tracking
      const user = users.find(u => u.id === userId);
      const taskTitle = `${routine.name} - ${user?.name} - ${new Date().toLocaleDateString('he-IL')}`;
      
      await createTask({
        title: taskTitle,
        description: routine.description,
        assigneeId: userId,
        assigneeIds: [userId],
        departmentId: routine.departmentId,
        teamId: routine.teamId,
        priority: routine.priority,
        estimate: routine.estimatedTime / 60,
        dueDate: today,
        status: 'הושלם',
        scope: routine.scope || 'managerial',
        isDailyRoutine: true,
        routineId: routine.id,
        createdFrom: 'daily_routine',
        completedAt: new Date()
      });
      
      // Reload execution status
      await loadExecutionStatus(routine);
      
      // Update local state
      setCompletedToday(prev => new Set([...prev, routine.id]));
      
      alert(`השגרה סומנה כהושלמה עבור ${user?.name}!`);
    } catch (error) {
      console.error('Error marking routine as completed:', error);
      alert('שגיאה בסימון השגרה כהושלמה');
    }
  };

  // Undo routine completion for specific user
  const undoRoutineCompletion = async (routine, userId) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Find and delete execution record
      const executionsRef = collection(db, 'routineExecutions');
      const q = query(
        executionsRef,
        where('routineId', '==', routine.id),
        where('userId', '==', userId),
        where('executionDate', '==', today)
      );
      const snapshot = await getDocs(q);
      
      for (const doc of snapshot.docs) {
        await deleteDoc(doc.ref);
      }
      
      // Delete the task
      const completedTask = tasks.find(task => 
        task.routineId === routine.id && 
        task.assigneeId === userId &&
        task.dueDate === today && 
        task.status === 'הושלם' &&
        task.isDailyRoutine
      );
      
      if (completedTask) {
        await deleteDoc(doc(db, COLLECTIONS.TASKS || 'tasks', completedTask.id));
      }
      
      // Reload execution status
      await loadExecutionStatus(routine);
      
      // Check if any executions remain for this routine
      const remainingExecutions = await getRoutineExecutions(routine.id, today);
      if (remainingExecutions.length === 0) {
        setCompletedToday(prev => {
          const newSet = new Set([...prev]);
          newSet.delete(routine.id);
          return newSet;
        });
      }
      
      alert('ביטול הסימון בוצע בהצלחה!');
    } catch (error) {
      console.error('Error undoing completion:', error);
      alert('שגיאה בביטול הסימון');
    }
  };

  // Helper functions
  const getOrganizationalRoutines = () => {
    return routines.filter(routine => routine.scope === 'managerial');
  };

  const getPersonalRoutines = () => {
    return routines.filter(routine => routine.scope === 'personal' && routine.createdBy === currentUser.id);
  };

  const getFrequencyLabel = (frequency) => {
    switch (frequency) {
      case 'daily': return 'יומי';
      case 'weekly': return 'שבועי';
      case 'monthly': return 'חודשי';
        case 'quarterly': return 'רבעוני';    // וודא שזה קיים
    case 'yearly': return 'שנתי';         // וודא שזה קייםר
      default: return 'יומי';
    }
  };

  const getWeekdayName = (day) => {
    const days = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
    return days[day] || '';
  };
  // >>>>>>>>>>> הדבק את הפונקציה החדשה כאן! <<<<<<<<<<
  const getMonthName = (month) => {
    const months = [
      'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
      'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
    ];
    return months[month - 1] || '';
  };
  const resetForm = () => {
    setRoutineForm({
      name: '',
      description: '',
      assigneeId: '',
      assigneeIds: [],
      departmentId: '',
      teamId: '',
      priority: 'medium',
      estimatedTime: 30,
      dueTime: '09:00',
      scope: 'personal', // ברירת מחדל אישי לכולם
      isActive: true,
      frequency: 'daily',
      weeklyDay: 1,
      monthlyDate: 1,
         quarterlyMonth: 1,    // הוסף את זה
    quarterlyDay: 1,      // הוסף את זה
    yearlyMonth: 1,       // הוסף את זה
    yearlyDay: 1,         // הוסף את זה
      enableNotifications: true,
      notificationTime: '08:30'
    });
  };

  const handleCreateRoutine = async (e) => {
    e.preventDefault();
    try {
      // בדיקת הרשאות לפי תפקיד
      if (routineForm.scope === 'managerial') {
        const canCreateOrganizational = 
          currentUser?.role === 'team_manager' || 
          currentUser?.role === 'dept_manager' || 
          currentUser?.role === 'ceo' || 
          currentUser?.role === 'deputy_ceo' || 
          currentUser?.role === 'org_admin';
        
        if (!canCreateOrganizational) {
          alert('אין לך הרשאה ליצור שגרות ארגוניות');
          return;
        }
      }
      
      const routineData = {
        ...routineForm,
        assigneeIds: routineForm.assigneeIds.length > 0 ? routineForm.assigneeIds : [],
        createdBy: currentUser.id,
        createdAt: serverTimestamp()
      };
      
      const routineRef = await addDoc(collection(db, COLLECTIONS.DAILY_ROUTINES || 'dailyRoutines'), routineData);
      
      setShowNewRoutineModal(false);
      resetForm();
      
      // Add to local state
      const newRoutine = {
        id: routineRef.id,
        ...routineData,
        createdAt: new Date()
      };
      
      setRoutines(prevRoutines => [...prevRoutines, newRoutine]);
      
      alert('השגרה נוצרה בהצלחה!');
    } catch (error) {
      console.error('Error creating routine:', error);
      alert('שגיאה ביצירת השגרה היומית');
    }
  };

  const handleUpdateRoutine = async (e) => {
    e.preventDefault();
    try {
      // בדיקת הרשאות לפי תפקיד
      if (routineForm.scope === 'managerial') {
        const canCreateOrganizational = 
          currentUser?.role === 'team_manager' || 
          currentUser?.role === 'dept_manager' || 
          currentUser?.role === 'ceo' || 
          currentUser?.role === 'deputy_ceo' || 
          currentUser?.role === 'org_admin';
        
        if (!canCreateOrganizational) {
          alert('אין לך הרשאה לעדכן שגרות ארגוניות');
          return;
        }
      }
      
      const updateData = {
        ...routineForm,
        assigneeIds: routineForm.assigneeIds.length > 0 ? routineForm.assigneeIds : [],
        updatedBy: currentUser.id,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(doc(db, COLLECTIONS.DAILY_ROUTINES || 'dailyRoutines', editingRoutine.id), updateData);
      
      setShowEditRoutineModal(false);
      setEditingRoutine(null);
      resetForm();
      loadRoutines();
      
      alert('השגרה עודכנה בהצלחה!');
    } catch (error) {
      console.error('Error updating routine:', error);
      alert('שגיאה בעדכון השגרה היומית');
    }
  };

  const handleDeleteRoutine = async (routine) => {
    if (window.confirm(`האם אתה בטוח שברצונך למחוק את השגרה "${routine.name}"?`)) {
      try {
        await deleteDoc(doc(db, COLLECTIONS.DAILY_ROUTINES || 'dailyRoutines', routine.id));
        await cleanupOrphanedTasks(routine.id);
        
        loadRoutines();
        alert(`השגרה "${routine.name}" נמחקה בהצלחה!`);
      } catch (error) {
        console.error('Error deleting routine:', error);
        alert('שגיאה במחיקת השגרה היומית');
      }
    }
  };

  const openEditModal = (routine) => {
    setEditingRoutine(routine);
    setRoutineForm({
      name: routine.name || '',
      description: routine.description || '',
      assigneeId: routine.assigneeId || '',
      assigneeIds: routine.assigneeIds || [],
      departmentId: routine.departmentId || '',
      teamId: routine.teamId || '',
      priority: routine.priority || 'medium',
      estimatedTime: routine.estimatedTime || 30,
      dueTime: routine.dueTime || '09:00',
      scope: routine.scope || 'managerial',
      isActive: routine.isActive ?? true,
      frequency: routine.frequency || 'daily',
      weeklyDay: routine.weeklyDay || 1,
      monthlyDate: routine.monthlyDate || 1,
      quarterlyMonth: routine.quarterlyMonth || 1,    // הוסף
    quarterlyDay: routine.quarterlyDay || 1,          // הוסף
    yearlyMonth: routine.yearlyMonth || 1,           // הוסף
    yearlyDay: routine.yearlyDay || 1,               // הוסף
      enableNotifications: routine.enableNotifications ?? true,
      notificationTime: routine.notificationTime || '08:30'
    });
    setShowEditRoutineModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">טוען שגרות...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-xl flex items-center justify-center">
                <RotateCcw className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              שגרות יומיות
            </h1>
            <p className="text-gray-600 mt-2 text-sm md:text-base">נהל משימות שחוזרות על עצמן</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => {
                resetForm();
                setShowNewRoutineModal(true);
              }}
              className="px-4 md:px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 flex items-center justify-center gap-2 font-medium transition-all shadow-lg min-h-[44px] touch-manipulation"
            >
              <Plus className="w-5 h-5" />
              שגרה חדשה
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-6 mb-6 md:mb-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">סה"כ שגרות</p>
              <p className="text-3xl font-bold text-gray-900">{routines.length}</p>
            </div>
            <RotateCcw className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">שגרות פעילות</p>
              <p className="text-3xl font-bold text-green-600">{routines.filter(r => r.isActive).length}</p>
            </div>
            <CheckSquare className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">שגרות להיום</p>
              <p className="text-3xl font-bold text-purple-600">
                {routines.filter(r => r.isActive && isRoutineScheduledForToday(r)).length}
              </p>
            </div>
            <CalendarDays className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">הושלמו היום</p>
              <p className="text-3xl font-bold text-orange-600">{completedToday.size}</p>
            </div>
            <UserCheck className="w-8 h-8 text-orange-600" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">זמן כולל (דקות)</p>
              <p className="text-3xl font-bold text-indigo-600">
                {routines.filter(r => r.isActive && isRoutineScheduledForToday(r))
                  .reduce((sum, r) => sum + (r.estimatedTime || 0), 0)}
              </p>
            </div>
            <Clock className="w-8 h-8 text-indigo-600" />
          </div>
        </div>
      </div>

      {/* Tabs and Filters */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('organizational')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'organizational'
                    ? 'text-purple-600 border-purple-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                שגרות ארגוניות
                <span className="mr-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                  {getOrganizationalRoutines().length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('personal')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'personal'
                    ? 'text-blue-600 border-blue-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                שגרות אישיות
                <span className="mr-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                  {getPersonalRoutines().length}
                </span>
              </button>
            </div>
            
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm min-h-[40px] w-full sm:w-auto"
              >
                <option value="">כל המחלקות</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
              <select
                value={filterTeam}
                onChange={(e) => setFilterTeam(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm min-h-[40px] w-full sm:w-auto"
              >
                <option value="">כל הצוותים</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Routines List */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {activeTab === 'organizational' ? 'שגרות ארגוניות' : 'שגרות אישיות'}
          </h2>
        </div>
        
        {(() => {
          let currentRoutines = activeTab === 'organizational' ? getOrganizationalRoutines() : getPersonalRoutines();
          
          // Apply filters
          if (filterDepartment) {
            currentRoutines = currentRoutines.filter(r => r.departmentId === filterDepartment);
          }
          if (filterTeam) {
            currentRoutines = currentRoutines.filter(r => r.teamId === filterTeam);
          }
          
          if (currentRoutines.length === 0) {
            return (
              <div className="text-center py-12">
                <RotateCcw className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">אין שגרות להצגה</p>
              </div>
            );
          }
          
                                return (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 p-6 animate-fade-in">
                  {currentRoutines.map((routine) => {
                    const assigneeIds = routine.assigneeIds || [routine.assigneeId];
                    const assignees = assigneeIds.length > 0 && assigneeIds[0] 
                      ? assigneeIds.map(id => users.find(u => u.id === id)).filter(Boolean)
                      : [users.find(u => u.id === routine.createdBy)].filter(Boolean);
                    const department = departments.find(d => d.id === routine.departmentId);
                    const isScheduledToday = isRoutineScheduledForToday(routine);
                    const isCompleted = completedToday.has(routine.id);
                    
                    return (
                      <div 
                        key={routine.id} 
                        className={`bg-white rounded-xl shadow-lg border hover:shadow-xl transition-all duration-300 overflow-hidden group ${
                          !isScheduledToday ? 'opacity-70' : ''
                        } ${isCompleted ? 'border-green-300 bg-green-50/30' : 'border-gray-200'}`}
                      >
                        {/* Header */}
                        <div className={`p-4 border-b ${isCompleted ? 'bg-green-100' : 'bg-gradient-to-r from-blue-50 to-indigo-50'}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-bold text-lg text-gray-900 mb-1">{routine.name}</h3>
                              {routine.description && (
                                <p className="text-gray-600 text-sm line-clamp-2">{routine.description}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mr-3">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                !isScheduledToday 
                                  ? 'bg-gray-100 text-gray-600'
                                  : isCompleted 
                                    ? 'bg-blue-100 text-blue-800'
                                    : routine.isActive 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-red-100 text-red-800'
                              }`}>
                                {!isScheduledToday ? 'לא מתוכנן להיום' : isCompleted ? 'בוצע' : routine.isActive ? 'פעיל' : 'לא פעיל'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-4 space-y-4">
                          {/* Status Button */}
                          <div className="flex justify-center">
                            {canViewRoutineStatus(routine) ? (
                              <button
                                onClick={() => openExecutionStatusModal(routine)}
                                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all font-medium ${
                                  isCompleted 
                                    ? 'bg-green-500 hover:bg-green-600 text-white shadow-md' 
                                    : 'bg-blue-500 hover:bg-blue-600 text-white shadow-md'
                                }`}
                                disabled={!isScheduledToday}
                              >
                                <Eye className="w-4 h-4" />
                                <span>{isCompleted ? 'הושלמה' : 'צפה בסטטוס'}</span>
                              </button>
                            ) : (
                              <div className="px-4 py-2 bg-gray-100 text-gray-500 rounded-lg flex items-center gap-2">
                                <Eye className="w-4 h-4" />
                                <span>אין הרשאה לצפייה</span>
                              </div>
                            )}
                          </div>

                          {/* Assignees */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                              <Users className="w-4 h-4 text-blue-600" />
                              <span>אחראים</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {assignees.slice(0, 3).map(user => (
                                <span key={user.id} className="inline-flex items-center px-2.5 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                  {user.name}
                                </span>
                              ))}
                              {assignees.length > 3 && (
                                <span className="inline-flex items-center px-2.5 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                  +{assignees.length - 3} נוספים
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Details Grid */}
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            {/* Frequency */}
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 text-gray-700 font-medium">
                                <CalendarRange className="w-4 h-4 text-purple-600" />
                                <span>תדירות</span>
                              </div>
                              <div className="text-gray-900">
                                {getFrequencyLabel(routine.frequency)}
                                {routine.frequency === 'weekly' && (
                                  <div className="text-xs text-gray-500">
                                    כל {getWeekdayName(routine.weeklyDay)}
                                  </div>
                                )}
                                {routine.frequency === 'monthly' && (
                                  <div className="text-xs text-gray-500">
                                    כל {routine.monthlyDate} בחודש
                                  </div>
                                )}
                                {routine.frequency === 'quarterly' && (
  <div className="text-xs text-gray-500">
    חודש {routine.quarterlyMonth} ברבעון, יום {routine.quarterlyDay}
  </div>
)}
{routine.frequency === 'yearly' && (
  <div className="text-xs text-gray-500">
    {routine.yearlyDay} ב{getMonthName(routine.yearlyMonth)}
  </div>
)}
                              </div>
                            </div>

                            {/* Department */}
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 text-gray-700 font-medium">
                                <Building2 className="w-4 h-4 text-orange-600" />
                                <span>מחלקה</span>
                              </div>
                              <div className="text-gray-900">
                                {department?.name || 'לא הוגדר'}
                              </div>
                            </div>

                            {/* Estimated Time */}
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 text-gray-700 font-medium">
                                <Clock className="w-4 h-4 text-green-600" />
                                <span>זמן משוער</span>
                              </div>
                              <div className="text-gray-900">
                                {routine.estimatedTime} דקות
                              </div>
                            </div>

                            {/* Due Time */}
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 text-gray-700 font-medium">
                                <Clock className="w-4 h-4 text-yellow-600" />
                                <span>שעת יעד</span>
                              </div>
                              <div className="text-gray-900">
                                {routine.dueTime || 'לא הוגדר'}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Actions Footer */}
                        {((routine.scope === 'personal' && routine.createdBy === currentUser.id) || 
                          (routine.scope === 'managerial' && (currentUser.role === 'ceo' || routine.createdBy === currentUser.id))) && (
                          <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-end gap-2">
                            <button
                              onClick={() => openEditModal(routine)}
                              className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 p-2 rounded-lg transition-all duration-200 touch-manipulation"
                              title="ערוך שגרה"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteRoutine(routine)}
                              className="text-red-600 hover:text-red-800 hover:bg-red-100 p-2 rounded-lg transition-all duration-200 touch-manipulation"
                              title="מחק שגרה"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
            </div>
          );
        })()}
      </div>

      {/* Execution Status Modal */}
      {showExecutionStatusModal && selectedRoutineForStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">סטטוס ביצוע - {selectedRoutineForStatus.name}</h2>
              <p className="text-indigo-100 text-sm mt-1">
  תאריך: {new Date().toLocaleDateString('he-IL')} | 
  תדירות: {getFrequencyLabel(selectedRoutineForStatus.frequency)} |
  {selectedRoutineForStatus.scope === 'managerial' 
    ? `שגרה ארגונית - ${executionStatuses.length} עובדים`
    : 'שגרה אישית'
  }
</p>
                </div>
                <button
                  onClick={() => {
                    setShowExecutionStatusModal(false);
                    setSelectedRoutineForStatus(null);
                  }}
                  className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="p-4 bg-gray-50 border-b">
              <div className="flex gap-4 items-center">
                <Filter className="w-4 h-4 text-gray-500" />
               <select 
  className="px-3 py-1 border rounded-lg text-sm"
  onChange={(e) => {
    const value = e.target.value;
    if (value === 'all') {
      setExecutionStatuses(allExecutionStatuses);
    } else {
      const filtered = allExecutionStatuses.filter(s => s.status === value);
      setExecutionStatuses(filtered);
    }
  }}
>
  <option value="all">כל הסטטוסים</option>
  <option value="completed">ביצעו שגרה</option>
  <option value="pending">לא ביצעו שגרה</option>
</select>

              </div>
            </div>

            {/* Status List */}
            <div className="p-6">
              <div className="space-y-3">
                {executionStatuses.map((status) => (
                  <div key={status.userId} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        status.status === 'completed' ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        {status.status === 'completed' ? (
                          <UserCheck className="w-5 h-5 text-green-600" />
                        ) : (
                          <UserX className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{status.userName}</div>
                        <div className="text-sm text-gray-500">
                          {status.department} {status.team && `- ${status.team}`}
                        </div>
                        {status.status === 'completed' && status.completedAt && (
                          <div className="text-xs text-green-600 mt-1">
                            הושלם בשעה {status.completedAt.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      status.status === 'completed' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {status.status === 'completed' ? 'ביצע שגרה' : 'לא ביצע שגרה'}
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="mt-6 pt-6 border-t">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {executionStatuses.filter(s => s.status === 'completed').length}
                    </p>
                    <p className="text-sm text-gray-600">בוצעו</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-600">
                      {executionStatuses.filter(s => s.status === 'pending').length}
                    </p>
                    <p className="text-sm text-gray-600">לא ביצעו</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      {executionStatuses.length > 0 
                        ? Math.round((executionStatuses.filter(s => s.status === 'completed').length / executionStatuses.length) * 100)
                        : 0}%
                    </p>
                    <p className="text-sm text-gray-600">אחוז השלמה</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Routine Modal */}
      {showNewRoutineModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-blue-600 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">שגרה חדשה</h2>
                  <p className="text-green-100 text-sm mt-1">צור משימה שתחזור על עצמה</p>
                </div>
                <button
                  onClick={() => setShowNewRoutineModal(false)}
                  className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleCreateRoutine} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      שם השגרה
                    </label>
                    <input
                      type="text"
                      value={routineForm.name}
                      onChange={(e) => setRoutineForm({...routineForm, name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="לדוגמה: בדיקת מייל יומית"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      תיאור השגרה
                    </label>
                    <textarea
                      value={routineForm.description}
                      onChange={(e) => setRoutineForm({...routineForm, description: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      rows="3"
                      placeholder="תאר מה כולל השגרה..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      תדירות
                    </label>
                    <select
                      value={routineForm.frequency}
                      onChange={(e) => setRoutineForm({...routineForm, frequency: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="daily">יומי</option>
                      <option value="weekly">שבועי</option>
                      <option value="monthly">חודשי</option>
                      <option value="quarterly">רבעוני</option>
<option value="yearly">שנתי</option>
                    </select>
                  </div>

                  {routineForm.frequency === 'weekly' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        יום בשבוע
                      </label>
                      <select
                        value={routineForm.weeklyDay}
                        onChange={(e) => setRoutineForm({...routineForm, weeklyDay: parseInt(e.target.value)})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="0">ראשון</option>
                        <option value="1">שני</option>
                        <option value="2">שלישי</option>
                        <option value="3">רביעי</option>
                        <option value="4">חמישי</option>
                        <option value="5">שישי</option>
                        <option value="6">שבת</option>
                      </select>
                    </div>
                  )}

                  {routineForm.frequency === 'monthly' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        יום בחודש
                      </label>
                      <input
                        type="number"
                        value={routineForm.monthlyDate}
                        onChange={(e) => setRoutineForm({...routineForm, monthlyDate: parseInt(e.target.value) || 1})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="1"
                        max="31"
                      />
                    </div>
                  )}
{routineForm.frequency === 'quarterly' && (
  <>
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        חודש ברבעון
      </label>
      <select
        value={routineForm.quarterlyMonth}
        onChange={(e) => setRoutineForm({...routineForm, quarterlyMonth: parseInt(e.target.value)})}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="1">חודש ראשון ברבעון</option>
        <option value="2">חודש שני ברבעון</option>
        <option value="3">חודש שלישי ברבעון</option>
      </select>
      <p className="text-xs text-gray-500 mt-1">
        רבעון 1: ינואר-מרץ | רבעון 2: אפריל-יוני | רבעון 3: יולי-ספטמבר | רבעון 4: אוקטובר-דצמבר
      </p>
    </div>
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        יום בחודש
      </label>
      <input
        type="number"
        value={routineForm.quarterlyDay}
        onChange={(e) => setRoutineForm({...routineForm, quarterlyDay: parseInt(e.target.value) || 1})}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        min="1"
        max="31"
      />
    </div>
  </>
)}

{routineForm.frequency === 'yearly' && (
  <>
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        חודש בשנה
      </label>
      <select
        value={routineForm.yearlyMonth}
        onChange={(e) => setRoutineForm({...routineForm, yearlyMonth: parseInt(e.target.value)})}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
          <option key={month} value={month}>{getMonthName(month)}</option>
        ))}
      </select>
    </div>
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        יום בחודש
      </label>
      <input
        type="number"
        value={routineForm.yearlyDay}
        onChange={(e) => setRoutineForm({...routineForm, yearlyDay: parseInt(e.target.value) || 1})}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        min="1"
        max="31"
      />
    </div>
  </>
)}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      מחלקה
                    </label>
                    <select
                      value={routineForm.departmentId}
                      onChange={(e) => setRoutineForm({...routineForm, departmentId: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">בחר מחלקה</option>
                      <option value="all">כל המחלקות</option>
                      {(() => {
                        // סינון מחלקות לפי תפקיד
                        let availableDepts = departments;
                        
                        if (currentUser?.role === 'dept_manager') {
                          // מנהל מחלקה רואה רק את המחלקה שלו
                          availableDepts = departments.filter(d => d.id === currentUser.departmentId);
                        } else if (currentUser?.role === 'team_manager') {
                          // מנהל צוות רואה את המחלקה שלו
                          availableDepts = departments.filter(d => d.id === currentUser.departmentId);
                        } else if (currentUser?.role === 'employee') {
                          // עובד רואה את המחלקה שלו (אם בכלל צריך)
                          availableDepts = departments.filter(d => d.id === currentUser.departmentId);
                        }
                        // מנכ"ל וסמנכ"ל רואים את כל המחלקות
                        
                        return availableDepts.map(dept => (
                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ));
                      })()}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      תחום
                    </label>
                    <select
                      value={routineForm.scope}
                      onChange={(e) => setRoutineForm({...routineForm, scope: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {/* כל המשתמשים יכולים ליצור שגרות אישיות */}
                      <option value="personal">אישי</option>
                      
                      {/* רק מנהלים יכולים ליצור שגרות ארגוניות */}
                      {(currentUser?.role === 'team_manager' || 
                        currentUser?.role === 'dept_manager' || 
                        currentUser?.role === 'ceo' || 
                        currentUser?.role === 'deputy_ceo' || 
                        currentUser?.role === 'org_admin') && (
                        <option value="managerial">ארגוני</option>
                      )}
                    </select>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      אחראים על השגרה
                    </label>
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded-xl p-3">
                      {users
                        .filter(u => {
                          if (!u.isActive) return false;
                          
                          // אם זו שגרה אישית, רק המשתמש הנוכחי
                          if (routineForm.scope === 'personal') {
                            return u.id === currentUser.id;
                          }
                          
                          // אם זו שגרה ארגונית, תלוי בתפקיד
                          if (routineForm.scope === 'managerial') {
                            if (currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo' || currentUser?.role === 'org_admin') {
                              // מנכ"ל וסמנכ"ל יכולים להקצות לכל החברה
                              if (routineForm.departmentId === 'all' || !routineForm.departmentId) {
                                return true; // כל העובדים
                              } else {
                                return u.departmentId === routineForm.departmentId;
                              }
                            } else if (currentUser?.role === 'dept_manager') {
                              // מנהל מחלקה יכול להקצות רק למחלקה שלו
                              return u.departmentId === currentUser.departmentId;
                            } else if (currentUser?.role === 'team_manager') {
                              // מנהל צוות יכול להקצות רק לצוות שלו
                              return u.teamId === currentUser.teamId || u.id === currentUser.id;
                            }
                          }
                          
                          return false;
                        })
                        .map(user => (
                          <label key={user.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                            <input
                              type="checkbox"
                              checked={routineForm.assigneeIds.includes(user.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setRoutineForm({
                                    ...routineForm,
                                    assigneeIds: [...routineForm.assigneeIds, user.id]
                                  });
                                } else {
                                  setRoutineForm({
                                    ...routineForm,
                                    assigneeIds: routineForm.assigneeIds.filter(id => id !== user.id)
                                  });
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="mr-2 text-sm">{user.name}</span>
                          </label>
                        ))}
                    </div>
                    {routineForm.assigneeIds.length === 0 && (
                      <p className="text-xs text-gray-500 mt-1">אם לא נבחרו אחראים, כל העובדים הרלוונטיים יהיו אחראים</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      עדיפות
                    </label>
                    <select
                      value={routineForm.priority}
                      onChange={(e) => setRoutineForm({...routineForm, priority: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="important_urgent">🔴 חשוב ודחוף</option>
                      <option value="important_not_urgent">🟡 חשוב ולא דחוף</option>
                      <option value="not_important_urgent">🟠 לא חשוב ודחוף</option>
                      <option value="not_important_not_urgent">🟢 לא חשוב ולא דחוף</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        זמן משוער (דקות)
                      </label>
                      <input
                        type="number"
                        value={routineForm.estimatedTime}
                        onChange={(e) => setRoutineForm({...routineForm, estimatedTime: parseInt(e.target.value) || 0})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="1"
                        placeholder="30"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        שעת יעד
                      </label>
                      <input
                        type="time"
                        value={routineForm.dueTime}
                        onChange={(e) => setRoutineForm({...routineForm, dueTime: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isActive"
                        checked={routineForm.isActive}
                        onChange={(e) => setRoutineForm({...routineForm, isActive: e.target.checked})}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="isActive" className="mr-3 text-sm font-medium text-gray-700">
                        שגרה פעילה
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="enableNotifications"
                        checked={routineForm.enableNotifications}
                        onChange={(e) => setRoutineForm({...routineForm, enableNotifications: e.target.checked})}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="enableNotifications" className="mr-3 text-sm font-medium text-gray-700">
                        שלח תזכורות
                      </label>
                    </div>

                    {routineForm.enableNotifications && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          שעת תזכורת
                        </label>
                        <input
                          type="time"
                          value={routineForm.notificationTime}
                          onChange={(e) => setRoutineForm({...routineForm, notificationTime: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex gap-4 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowNewRoutineModal(false)}
                    className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-all"
                  >
                    בטל
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white hover:from-green-700 hover:to-blue-700 rounded-xl font-medium transition-all shadow-lg"
                  >
                    צור שגרה
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Routine Modal */}
      {showEditRoutineModal && editingRoutine && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">עריכת שגרה</h2>
                  <p className="text-blue-100 text-sm mt-1">עדכן פרטי השגרה</p>
                </div>
                <button
                  onClick={() => {
                    setShowEditRoutineModal(false);
                    setEditingRoutine(null);
                  }}
                  className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Form - same structure as create form */}
            <form onSubmit={handleUpdateRoutine} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      שם השגרה
                    </label>
                    <input
                      type="text"
                      value={routineForm.name}
                      onChange={(e) => setRoutineForm({...routineForm, name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      תיאור השגרה
                    </label>
                    <textarea
                      value={routineForm.description}
                      onChange={(e) => setRoutineForm({...routineForm, description: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      rows="3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      תדירות
                    </label>
                    <select
                      value={routineForm.frequency}
                      onChange={(e) => setRoutineForm({...routineForm, frequency: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="daily">יומי</option>
                      <option value="weekly">שבועי</option>
                      <option value="monthly">חודשי</option>
                      <option value="quarterly">רבעוני</option>
<option value="yearly">שנתי</option>
                    </select>
                  </div>

                  {routineForm.frequency === 'weekly' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        יום בשבוע
                      </label>
                      <select
                        value={routineForm.weeklyDay}
                        onChange={(e) => setRoutineForm({...routineForm, weeklyDay: parseInt(e.target.value)})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="0">ראשון</option>
                        <option value="1">שני</option>
                        <option value="2">שלישי</option>
                        <option value="3">רביעי</option>
                        <option value="4">חמישי</option>
                        <option value="5">שישי</option>
                        <option value="6">שבת</option>
                      </select>
                    </div>
                  )}

                  {routineForm.frequency === 'monthly' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        יום בחודש
                      </label>
                      <input
                        type="number"
                        value={routineForm.monthlyDate}
                        onChange={(e) => setRoutineForm({...routineForm, monthlyDate: parseInt(e.target.value) || 1})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="1"
                        max="31"
                      />
                    </div>
                  )}
{routineForm.frequency === 'quarterly' && (
  <>
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        חודש ברבעון
      </label>
      <select
        value={routineForm.quarterlyMonth}
        onChange={(e) => setRoutineForm({...routineForm, quarterlyMonth: parseInt(e.target.value)})}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="1">חודש ראשון ברבעון</option>
        <option value="2">חודש שני ברבעון</option>
        <option value="3">חודש שלישי ברבעון</option>
      </select>
      <p className="text-xs text-gray-500 mt-1">
        רבעון 1: ינואר-מרץ | רבעון 2: אפריל-יוני | רבעון 3: יולי-ספטמבר | רבעון 4: אוקטובר-דצמבר
      </p>
    </div>
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        יום בחודש
      </label>
      <input
        type="number"
        value={routineForm.quarterlyDay}
        onChange={(e) => setRoutineForm({...routineForm, quarterlyDay: parseInt(e.target.value) || 1})}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        min="1"
        max="31"
      />
    </div>
  </>
)}

{routineForm.frequency === 'yearly' && (
  <>
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        חודש בשנה
      </label>
      <select
        value={routineForm.yearlyMonth}
        onChange={(e) => setRoutineForm({...routineForm, yearlyMonth: parseInt(e.target.value)})}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
          <option key={month} value={month}>{getMonthName(month)}</option>
        ))}
      </select>
    </div>
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        יום בחודש
      </label>
      <input
        type="number"
        value={routineForm.yearlyDay}
        onChange={(e) => setRoutineForm({...routineForm, yearlyDay: parseInt(e.target.value) || 1})}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        min="1"
        max="31"
      />
    </div>
  </>
)}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      מחלקה
                    </label>
                    <select
                      value={routineForm.departmentId}
                      onChange={(e) => setRoutineForm({...routineForm, departmentId: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">בחר מחלקה</option>
                      <option value="all">כל המחלקות</option>
                      {(() => {
                        // סינון מחלקות לפי תפקיד
                        let availableDepts = departments;
                        
                        if (currentUser?.role === 'dept_manager') {
                          // מנהל מחלקה רואה רק את המחלקה שלו
                          availableDepts = departments.filter(d => d.id === currentUser.departmentId);
                        } else if (currentUser?.role === 'team_manager') {
                          // מנהל צוות רואה את המחלקה שלו
                          availableDepts = departments.filter(d => d.id === currentUser.departmentId);
                        } else if (currentUser?.role === 'employee') {
                          // עובד רואה את המחלקה שלו (אם בכלל צריך)
                          availableDepts = departments.filter(d => d.id === currentUser.departmentId);
                        }
                        // מנכ"ל וסמנכ"ל רואים את כל המחלקות
                        
                        return availableDepts.map(dept => (
                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ));
                      })()}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      תחום
                    </label>
                    <select
                      value={routineForm.scope}
                      onChange={(e) => setRoutineForm({...routineForm, scope: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {/* כל המשתמשים יכולים ליצור שגרות אישיות */}
                      <option value="personal">אישי - רק אני</option>
                      
                      {/* רק מנהלים יכולים ליצור שגרות ארגוניות */}
                      {(currentUser?.role === 'team_manager' || 
                        currentUser?.role === 'dept_manager' || 
                        currentUser?.role === 'ceo' || 
                        currentUser?.role === 'deputy_ceo' || 
                        currentUser?.role === 'org_admin') && (
                        <option value="managerial">ארגוני - כל העובדים</option>
                      )}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {routineForm.scope === 'personal' 
                        ? 'שגרה זו תופיע רק אצלך' 
                        : 'שגרה זו תופיע אצל כל העובדים'}
                    </p>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      אחראים על השגרה
                    </label>
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded-xl p-3">
                      {users
                        .filter(u => {
                          if (!u.isActive) return false;
                          
                          // אם זו שגרה אישית, רק המשתמש הנוכחי
                          if (routineForm.scope === 'personal') {
                            return u.id === currentUser.id;
                          }
                          
                          // אם זו שגרה ארגונית, תלוי בתפקיד
                          if (routineForm.scope === 'managerial') {
                            if (currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo' || currentUser?.role === 'org_admin') {
                              // מנכ"ל וסמנכ"ל יכולים להקצות לכל החברה
                              if (routineForm.departmentId === 'all' || !routineForm.departmentId) {
                                return true; // כל העובדים
                              } else {
                                return u.departmentId === routineForm.departmentId;
                              }
                            } else if (currentUser?.role === 'dept_manager') {
                              // מנהל מחלקה יכול להקצות רק למחלקה שלו
                              return u.departmentId === currentUser.departmentId;
                            } else if (currentUser?.role === 'team_manager') {
                              // מנהל צוות יכול להקצות רק לצוות שלו
                              return u.teamId === currentUser.teamId || u.id === currentUser.id;
                            }
                          }
                          
                          return false;
                        })
                        .map(user => (
                          <label key={user.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                            <input
                              type="checkbox"
                              checked={routineForm.assigneeIds.includes(user.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setRoutineForm({
                                    ...routineForm,
                                    assigneeIds: [...routineForm.assigneeIds, user.id]
                                  });
                                } else {
                                  setRoutineForm({
                                    ...routineForm,
                                    assigneeIds: routineForm.assigneeIds.filter(id => id !== user.id)
                                  });
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="mr-2 text-sm">{user.name}</span>
                          </label>
                        ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      עדיפות
                    </label>
                    <select
                      value={routineForm.priority}
                      onChange={(e) => setRoutineForm({...routineForm, priority: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="important_urgent">🔴 חשוב ודחוף</option>
                      <option value="important_not_urgent">🟡 חשוב ולא דחוף</option>
                      <option value="not_important_urgent">🟠 לא חשוב ודחוף</option>
                      <option value="not_important_not_urgent">🟢 לא חשוב ולא דחוף</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        זמן משוער (דקות)
                      </label>
                      <input
                        type="number"
                        value={routineForm.estimatedTime}
                        onChange={(e) => setRoutineForm({...routineForm, estimatedTime: parseInt(e.target.value) || 0})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        שעת יעד
                      </label>
                      <input
                        type="time"
                        value={routineForm.dueTime}
                        onChange={(e) => setRoutineForm({...routineForm, dueTime: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="editIsActive"
                        checked={routineForm.isActive}
                        onChange={(e) => setRoutineForm({...routineForm, isActive: e.target.checked})}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="editIsActive" className="mr-3 text-sm font-medium text-gray-700">
                        שגרה פעילה
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="editEnableNotifications"
                        checked={routineForm.enableNotifications}
                        onChange={(e) => setRoutineForm({...routineForm, enableNotifications: e.target.checked})}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="editEnableNotifications" className="mr-3 text-sm font-medium text-gray-700">
                        שלח תזכורות
                      </label>
                    </div>

                    {routineForm.enableNotifications && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          שעת תזכורת
                        </label>
                        <input
                          type="time"
                          value={routineForm.notificationTime}
                          onChange={(e) => setRoutineForm({...routineForm, notificationTime: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex gap-4 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditRoutineModal(false);
                      setEditingRoutine(null);
                    }}
                    className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-all"
                  >
                    בטל
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 rounded-xl font-medium transition-all shadow-lg"
                  >
                    שמור שינויים
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyRoutines;